import axios from 'axios'

import {
  EExportJobType,
  TExportJobPollResponse,
  TStartExportJobResponse,
  EExportJobStatus,
  EExportDownloadError,
} from '~/features/exports/models'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import { HttpServiceBaseResponse } from '~/services/http/models'
import pathBuilder from '~/utils/api'

const getHttpErrorStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined

const parseStartResponse = (
  response: HttpServiceBaseResponse<TStartExportJobResponse>,
): string => {
  const jobId = response.data?.jobId
  if (!jobId) {
    throw new Error('Export did not return a job id')
  }
  return jobId
}

const parsePollResponse = (
  response: HttpServiceBaseResponse<TExportJobPollResponse>,
): TExportJobPollResponse => {
  const { data } = response
  if (!data?.jobId || !data.status) {
    throw new Error('Export status response was empty')
  }
  return data
}

const handleStartError = (error: unknown): never => {
  if (getHttpErrorStatus(error) === 429) {
    throw new Error(
      'Another export is already running. Please try again shortly.',
    )
  }
  if (error instanceof Error) {
    throw error
  }
  throw new Error('Failed to start export')
}

export const startContactsExportJob = async (
  query: Record<string, string>,
): Promise<string> => {
  try {
    const response = await HttpService.post<TStartExportJobResponse>(
      { path: EServerPath.contactsExport, query },
      EHttpService.internal,
    )
    return parseStartResponse(response)
  } catch (error) {
    return handleStartError(error)
  }
}

export const startOrgMetricsExportJob = async (
  query: Record<string, string>,
): Promise<string> => {
  try {
    const response = await HttpService.post<TStartExportJobResponse>(
      { path: EServerPath.reportingOrgMetricsExport, query },
      EHttpService.internal,
    )
    return parseStartResponse(response)
  } catch (error) {
    return handleStartError(error)
  }
}

export const pollExportJob = async (
  jobId: string,
  type: EExportJobType,
  options?: { presignDownload?: boolean },
): Promise<TExportJobPollResponse | null> => {
  const path =
    type === EExportJobType.contacts
      ? pathBuilder(EServerPath.crmExportJob, { jobId })
      : pathBuilder(EServerPath.reportingExportJob, { jobId })

  const query = options?.presignDownload ? { download: '1' } : undefined

  try {
    const response = await HttpService.get<TExportJobPollResponse>(
      { path, query },
      EHttpService.internal,
    )
    return parsePollResponse(response)
  } catch (error) {
    if (getHttpErrorStatus(error) === 404) {
      return null
    }
    throw error
  }
}

export const downloadExportJob = async (
  jobId: string,
  type: EExportJobType,
): Promise<{ url: string; downloadFilename?: string }> => {
  const status = await pollExportJob(jobId, type, { presignDownload: true })
  if (!status) {
    throw new Error(EExportDownloadError.expired)
  }
  if (status.status !== EExportJobStatus.complete || !status.url) {
    throw new Error(EExportDownloadError.notReady)
  }
  return {
    url: status.url,
    downloadFilename: status.downloadFilename,
  }
}
